/**
 * The timeline: the build in the order it happened.
 *
 * Ported from the reference page, which made this the spine of the whole
 * article — a date column, a marked line down the middle, and a bubble card for
 * each step. It is the one structure that carries the project's actual
 * argument, which is that the design changed because of what a fitting taught
 * them.
 *
 * Each step is a two-column card: the media on one side and the description on
 * the other. A step with no media is a full-width card, so the sequence does
 * not develop a hole where a photograph happens not to exist.
 *
 * One block, not one per step. `remidus-api-core` renders the served HTML and
 * drops any block type it does not know, so every project block here is a
 * `paragraph` carrying a `variant` — and a spine drawn down a run of sibling
 * blocks cannot be expressed that way. Holding the steps as data in one block
 * also keeps them in order by construction rather than by convention.
 */

import { asset, RemidusImage, type BlockRenderer, type MediaItem } from '@remidus/web-core'

import { videoFigure } from '../media-behaviour'

import { claimAnchor } from '../anchors'
import { diagram } from './diagrams'
import { viewerElement } from './viewer'

interface Step {
  date?: string
  year?: string
  kicker?: string
  title?: string
  text?: string
  media_id?: string
  /** Several pictures instead of one. A step whose story is a process rather
   *  than a moment gets a grid: one photograph of four hours of support removal
   *  is a still life, three of them are an account of it. */
  media_ids?: string[] | string
  /** Print settings, as label/value pairs. A row with no value still renders,
   *  showing a dash: the set of things worth recording about a print is the
   *  same whether or not this one was measured, and a card that quietly drops
   *  the rows nobody filled in reads as though they were never asked. */
  specs?: { label?: string; value?: string }[]
  /** CAD parts to show under the text: the file names in `models/cad/`,
   *  without extension. Each gets a small turning view and a link to the
   *  original STL, because an engineer reading this page wants the file, not a
   *  picture of it. */
  models?: string[]
  /** A drawn diagram instead of a photograph. Used where there is nothing to
   *  photograph: a measurement, a modelling method, a reason for a split. */
  diagram?: string
  /** Which side the media sits on. Alternating reads better than a column. */
  flip?: boolean
  /** A step that has not happened yet. Drawn as an outline rather than a
   *  filled bubble, so the sequence shows where it continues. */
  pending?: boolean
  /** Mount the CAD viewer inside this step's card. The value is the viewer
   *  block's own shape — its caption and part list — so the model can sit in
   *  the sequence as a numbered step rather than beside it as a slab. */
  viewer?: Record<string, unknown>
  /** Render this step's media as the layered collage rather than a grid —
   *  scissor-cut prints and living clips scattered over one stage. Built for
   *  the finale, where a tidy grid would file the payoff into evidence. */
  collage?: boolean
  /** Sub-parts of one step, each with a heading, its own prose and its own
   *  visual, rendered in order down the card. For a step that is really one
   *  piece of work with several moves in it: three cards would break the
   *  unit, one undifferentiated card would lose the order. A section's
   *  visual is either a named diagram or, with `media: "step"`, the step's
   *  own attached media, which keeps the slot machinery unchanged. */
  sections?: {
    heading?: string
    text?: string
    diagram?: string
    /** Which of the step's attached media this section shows: `'step'` for
     *  all of them, an index for one, a list of indices for a row of several.
     *  Indices, because the media ids are filled in by slot after authoring
     *  and the seeder never knows them. */
    media?: 'step' | number | number[]
  }[]
}

function paragraphs(into: HTMLElement, text: string): void {
  for (const part of text.split(/\n{2,}/)) {
    const node = document.createElement('p')
    node.textContent = part.trim()
    if (node.textContent) into.append(node)
  }
}

/** The media half of a step, or null when the step has none.
 *
 *  Core's own figure carries the caption, so a grid here is a grid of those
 *  rather than a second caption mechanism. */
/** The finale: the march photograph at the card's full height, with the
 *  day's other pictures and clips tacked around it as scissor-cut prints.
 *  Clips loop muted and pause off screen. Head positions in every piece were
 *  anchored by face tracking before cropping.
 */
function collageFor(items: MediaItem[]): HTMLElement | null {
  if (items.length < 1) return null
  const stage = document.createElement('div')
  stage.className = 'skin-collage'
  stage.setAttribute('role', 'group')
  stage.setAttribute('aria-label', 'Photographs and clips from the march')

  const [mainItem, ...rest] = items

  // The same photograph, drained and thrown out of focus, filling the card
  // behind everything else. It reuses the main picture's own file, so the
  // ground under the collage costs nothing on the wire and can never drift
  // out of step with the picture it is made from.
  const wash = document.createElement('div')
  wash.className = 'skin-collage__wash'
  wash.setAttribute('aria-hidden', 'true')
  const washImage = document.createElement('img')
  washImage.src = mainItem.url
  washImage.alt = ''
  washImage.loading = 'lazy'
  washImage.decoding = 'async'
  wash.append(washImage)
  stage.append(wash)

  // The photograph itself, at the card's full height.
  const hero = document.createElement('div')
  hero.className = 'skin-collage__hero'
  hero.append(RemidusImage(mainItem))
  stage.append(hero)

  const videos: HTMLVideoElement[] = []
  for (const [index, item] of rest.entries()) {
    const piece = document.createElement('div')
    piece.className = `skin-collage__piece skin-collage__piece--${(index % 5) + 1}`

    const cut = document.createElement('div')
    cut.className = 'skin-collage__cut'
    const inner = item.kind === 'video' ? videoFigure(item) : RemidusImage(item)
    cut.append(inner)

    const tape = document.createElement('span')
    tape.className = 'skin-collage__tape'
    tape.setAttribute('aria-hidden', 'true')

    piece.append(tape, cut)
    stage.append(piece)
    const video = inner.querySelector('video')
    if (video) {
      video.muted = true
      videos.push(video)
    }
  }

  // A looping clip below the fold is a warm phone for nothing.
  if (videos.length > 0) {
    const seen = new IntersectionObserver((entries) => {
      const on = entries[0]?.isIntersecting ?? false
      for (const video of videos) {
        if (on) void video.play().catch(() => undefined)
        else video.pause()
      }
    })
    seen.observe(stage)
  }
  return stage
}

function mediaFor(items: MediaItem[]): HTMLElement | null {
  if (items.length === 0) return null
  const holder = document.createElement('div')
  holder.className = 'skin-step__media'
  if (items.length > 1) holder.classList.add('skin-step__media--gallery')
  for (const item of items) {
    holder.append(item.kind === 'video' ? videoFigure(item) : RemidusImage(item))
  }
  return holder
}

/** The print settings, as a description list. Real `dt`/`dd`, because that is
 *  what this is, and because an answer engine quoting a print time should get
 *  the label with it. */
function specsFor(specs: Step['specs'], stepIndex: number): HTMLElement | null {
  const rows = (specs ?? []).filter((row) => row?.label)
  if (rows.length === 0) return null

  const list = document.createElement('dl')
  list.className = 'skin-step__specs'
  for (const [rowIndex, row] of rows.entries()) {
    // Each pair wrapped as one cell (dl > div is valid HTML for exactly
    // this), so the grid wraps settings as units and a label can never end
    // up in a different column from its value.
    const cell = document.createElement('div')
    cell.className = 'skin-step__spec'
    const term = document.createElement('dt')
    term.textContent = row.label ?? ''
    term.dataset.remField = `items.${stepIndex}.specs.${rowIndex}.label`
    const value = document.createElement('dd')
    const text = (row.value ?? '').trim()
    value.textContent = text || 'not recorded'
    value.dataset.remField = `items.${stepIndex}.specs.${rowIndex}.value`
    // "not recorded" is the display for an empty value, not the value; the
    // editor clears it on focus and never saves it back as text.
    value.dataset.remFieldPlaceholder = 'not recorded'
    if (!text) value.classList.add('skin-step__spec--blank')
    cell.append(term, value)
    list.append(cell)
  }
  return list
}

/** The CAD strip: one small turning view per part, plus the original file.
 *
 *  Deliberately not the full viewer block. That one carries an assembly, an
 *  explode control and a part list; this is a part on its own, and a second
 *  set of controls on a timeline card would be a second thing to learn for no
 *  gain. One light, one material, no controls.
 *
 *  Everything is deferred: three and the loader arrive on a dynamic import,
 *  and that import only fires when the strip is actually near the viewport.
 *  A page with four of these must not pay for any of them until it is read. */
function modelStrip(names: string[]): HTMLElement {
  const strip = document.createElement('div')
  strip.className = 'skin-cad'

  for (const name of names) {
    const cell = document.createElement('figure')
    cell.className = 'skin-cad__part'

    const stage = document.createElement('div')
    stage.className = 'skin-cad__stage'
    cell.append(stage)

    const label = document.createElement('figcaption')
    label.className = 'skin-cad__label'
    const link = document.createElement('a')
    link.href = asset(`models/cad/${name}.stl`)
    link.download = `${name}.stl`
    link.textContent = `${name}.stl`
    label.append(link)
    cell.append(label)

    strip.append(cell)
    mountPart(stage, name)
  }
  return strip
}

function mountPart(stage: HTMLElement, name: string): void {
  let started = false
  const observer = new IntersectionObserver(
    (entries) => {
      if (started || !entries.some((entry) => entry.isIntersecting)) return
      started = true
      observer.disconnect()
      void start()
    },
    { rootMargin: '200px' },
  )
  observer.observe(stage)

  async function start(): Promise<void> {
    const [THREE, { GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
      import('three'),
      import('three/examples/jsm/loaders/GLTFLoader.js'),
      import('three/examples/jsm/libs/meshopt_decoder.module.js'),
    ])
    const loader = new GLTFLoader()
    loader.setMeshoptDecoder(MeshoptDecoder)

    let gltf
    try {
      gltf = await loader.loadAsync(asset(`models/cad/${name}.glb`))
    } catch {
      // The link to the file is still there, which is the part that matters.
      stage.classList.add('is-empty')
      return
    }

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
    renderer.setPixelRatio(Math.min(1.5, window.devicePixelRatio))
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(32, 1, 0.05, 20)

    // No materials on these. They are engineering parts, they arrived without
    // any, and giving them one each would be inventing a finish the CAD does
    // not describe. One neutral surface for every part, so what you compare
    // between them is shape.
    const surface = new THREE.MeshStandardMaterial({ color: 0xc9ccd2, roughness: 0.62, metalness: 0 })
    const part = gltf.scene
    part.traverse((child) => {
      const mesh = child as InstanceType<typeof THREE.Mesh>
      if (mesh.isMesh) mesh.material = surface
    })

    const pivot = new THREE.Group()
    pivot.add(part)
    scene.add(pivot)
    scene.add(new THREE.HemisphereLight(0xffffff, 0xc8cfe0, 2.1))
    const key = new THREE.DirectionalLight(0xffffff, 1.5)
    key.position.set(2, 3, 4)
    scene.add(key)

    // Normalised to a unit sphere before framing.
    //
    // The CAD is in millimetres, so a part is a couple of hundred units across
    // and the distance that frames it lands far outside any sensible far
    // plane — which is why the first version drew nothing at all. Scaling the
    // part instead of stretching the frustum keeps near and far tight, which
    // is also what keeps the depth buffer honest on a small canvas.
    const box = new THREE.Box3().setFromObject(part)
    const size = box.getSize(new THREE.Vector3())
    const middle = box.getCenter(new THREE.Vector3())
    part.position.sub(middle)
    const radius = Math.max(size.length() / 2, 1e-6)
    part.scale.setScalar(1 / radius)
    part.position.multiplyScalar(1 / radius)
    // Framed from a sphere so no angle of the turn can clip a corner.
    camera.position.set(0, 0.35, 1.06 / Math.sin((32 * Math.PI) / 360))
    camera.lookAt(0, 0, 0)

    stage.append(renderer.domElement)
    const resize = (): void => {
      const side = stage.clientWidth || 1
      renderer.setSize(side, side, false)
      camera.aspect = 1
      camera.updateProjectionMatrix()
    }
    resize()
    const watcher = new ResizeObserver(resize)
    watcher.observe(stage)

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let visible = true
    const onScreen = new IntersectionObserver((entries) => {
      visible = entries.some((entry) => entry.isIntersecting)
    })
    onScreen.observe(stage)

    let frame = 0
    const tick = (): void => {
      frame = requestAnimationFrame(tick)
      if (!visible || document.hidden) return
      if (!still) pivot.rotation.y += 0.006
      renderer.render(scene, camera)
    }
    tick()

    window.addEventListener('pagehide', () => {
      cancelAnimationFrame(frame)
      watcher.disconnect()
      onScreen.disconnect()
      renderer.dispose()
    }, { once: true })
  }
}

export const renderTimeline: BlockRenderer = (block, ctx) => {
  const steps = (block.data?.items ?? []) as Step[]
  if (!Array.isArray(steps) || steps.length === 0) return null

  const list = document.createElement('ol')
  list.className = 'skin-timeline'

  // The spine is one element behind the whole list; motion.ts scales the
  // progress bar inside it as the reader moves down.
  const spine = document.createElement('span')
  spine.className = 'skin-timeline__spine'
  spine.setAttribute('aria-hidden', 'true')
  const progress = document.createElement('span')
  progress.className = 'skin-timeline__progress'
  spine.append(progress)
  list.append(spine)

  for (const [index, step] of steps.entries()) {
    const item = document.createElement('li')
    item.className = 'skin-step'
    // Edit mode reads this: clicking a card opens the timeline panel on that
    // step rather than at the top of a fourteen-step list. Inert outside edit
    // mode, and a skin that does not set it keeps the whole-block behaviour.
    item.dataset.remItem = String(index)
    // A link target and a menu entry. Named from the step's own title so it
    // survives another card being inserted above it, which has happened to
    // this timeline more than once.
    claimAnchor(item, step.title ?? step.kicker ?? `Step ${index + 1}`, step.date)

    const when = document.createElement('p')
    when.className = 'skin-step__date'
    // `data-rem-field` on the text-bearing nodes is the in-place editing
    // contract: the path names the string inside this block's data, and the
    // editor makes the node itself editable in edit mode. The renderer is
    // the one piece of code that knows which node came from which field, so
    // the paths are written here. Inert outside edit mode.
    if (step.date) {
      const strong = document.createElement('b')
      strong.textContent = step.date
      strong.dataset.remField = `items.${index}.date`
      when.append(strong)
    }
    if (step.year) {
      const year = document.createElement('span')
      year.textContent = step.year
      year.dataset.remField = `items.${index}.year`
      when.append(year)
    }
    item.append(when)

    const marker = document.createElement('span')
    marker.className = 'skin-step__marker'
    marker.setAttribute('aria-hidden', 'true')
    item.append(marker)

    const card = document.createElement('div')
    card.className = 'skin-bubble-surface skin-step__card'
    if (step.pending) {
      card.classList.add('skin-step__card--pending')
      item.classList.add('skin-step--pending')
    }

    // `media_ids` wins where a step has both, so a step can be given a second
    // picture without its first one having to move.
    //
    // Accepts a bare string as well as an array. An editor bundle older than
    // the `media-list` field type renders this field as a plain text box, and
    // one id typed into that arrives here as a string. Reading it as a single
    // id beats rendering nothing and beats throwing.
    const supplied = step.media_ids
    const many = typeof supplied === 'string' ? [supplied] : (supplied ?? [])
    const wanted = many.length ? many : [step.media_id]
    const resolved = wanted
      .map((id) => (id ? ctx.resolveMedia(id) : undefined))
      .filter((item): item is MediaItem => Boolean(item))
    const sections = Array.isArray(step.sections) ? step.sections : []
    // A sectioned step renders its media inside the section that discusses
    // it, so the card-level column stands down and the card stays one column.
    let media = sections.length > 0 || step.collage ? null : mediaFor(resolved)
    if (!media && sections.length === 0 && step.diagram) {
      const drawing = diagram(step.diagram)
      if (drawing) {
        media = document.createElement('div')
        media.className = 'skin-step__media skin-step__media--diagram'
        media.append(drawing)
      }
    }
    if (media) {
      // Wrap, not columns. A fixed media column is empty whenever the prose
      // outruns the picture and cramped whenever it does not; floated on its
      // side, the media takes exactly its own height and the words flow
      // around and under it, whatever the ratio of the two.
      card.classList.add('skin-step__card--wrap')
      if (step.flip) card.classList.add('skin-step__card--flip')
    }

    const body = document.createElement('div')
    body.className = 'skin-step__body'

    if (step.kicker) {
      const kicker = document.createElement('p')
      kicker.className = 'skin-pullout__kicker'
      kicker.textContent = step.kicker
      kicker.dataset.remField = `items.${index}.kicker`
      body.append(kicker)
    }
    if (step.title) {
      const title = document.createElement('h3')
      title.className = 'skin-step__title'
      title.textContent = step.title
      title.dataset.remField = `items.${index}.title`
      body.append(title)
    }
    if (step.text) {
      // The paragraphs live in one wrapper so the whole field edits as one
      // piece; a blank line in the edit is a paragraph break in the data.
      const prose = document.createElement('div')
      prose.dataset.remField = `items.${index}.text`
      prose.setAttribute('data-rem-field-multiline', '')
      paragraphs(prose, step.text)
      body.append(prose)
    }

    if (step.viewer) {
      const model = viewerElement(step.viewer, { anchor: false })
      model.classList.add('skin-viewer--instep')
      body.append(model)
    }

    if (step.collage) {
      const tableau = collageFor(resolved)
      if (tableau) {
        card.classList.add('skin-step__card--collage')
        body.append(tableau)
      }
    }

    // Each section is its own row: prose one side, its visual the other, and
    // the sides alternate down the card the way whole cards alternate down
    // the timeline. On a narrow screen the row stacks, prose first.
    // Visual first in the DOM: on a wide screen it floats and the prose
    // wraps around and under it — a float only shapes the lines that follow
    // it — and on a narrow screen the visual leads the stack, matching how
    // every plain card reads.
    for (const [sectionIndex, section] of sections.entries()) {
      const row = document.createElement('div')
      row.className = 'skin-step__section'

      const prose = document.createElement('div')
      prose.className = 'skin-step__section-body'
      if (section.heading) {
        const subhead = document.createElement('h4')
        subhead.className = 'skin-step__subhead'
        subhead.textContent = section.heading
        subhead.dataset.remField = `items.${index}.sections.${sectionIndex}.heading`
        prose.append(subhead)
      }
      if (section.text) {
        const sectionProse = document.createElement('div')
        sectionProse.dataset.remField = `items.${index}.sections.${sectionIndex}.text`
        sectionProse.setAttribute('data-rem-field-multiline', '')
        paragraphs(sectionProse, section.text)
        prose.append(sectionProse)
      }

      let visual: HTMLElement | null = null
      const which = section.media
      if (which === 'step') visual = mediaFor(resolved)
      else if (typeof which === 'number') {
        visual = mediaFor(resolved[which] ? [resolved[which]] : [])
      } else if (Array.isArray(which)) {
        visual = mediaFor(
          which
            .map((i) => resolved[i])
            .filter((item): item is MediaItem => Boolean(item)),
        )
      } else if (section.diagram) {
        const drawing = diagram(section.diagram)
        if (drawing) {
          visual = document.createElement('div')
          visual.className = 'skin-step__media skin-step__media--diagram'
          visual.append(drawing)
        }
      }
      if (visual) {
        visual.classList.add('skin-step__media--inline')
        if (section.heading || section.text) {
          row.classList.add('skin-step__section--split')
          if (section.diagram) row.classList.add('skin-step__section--diagram')
          if (sectionIndex % 2 === 1) row.classList.add('skin-step__section--flip')
          row.append(visual, prose)
        } else {
          row.append(visual)
        }
      } else {
        row.append(prose)
      }
      body.append(row)
    }

    const models = Array.isArray(step.models)
      ? step.models.map(String).filter(Boolean)
      : []

    const specs = specsFor(step.specs, index)
    if (specs) body.append(specs)

    // Media first in the DOM so it leads on a narrow screen, where the card
    // stacks; `--flip` swaps the columns visually on a wide one.
    if (media) card.append(media)

    // Where the CAD strip goes depends on how many parts it holds. A set
    // spans the whole card below both columns, where it reads as the row it
    // is. A single part is a continuation of the media column: it docks
    // under the video, aligned with it, and the text runs alongside both.
    const strip = models.length > 0 ? modelStrip(models) : null
    if (strip && media && models.length === 1) {
      // A lone part floats in behind the video on the same side, so the two
      // read as one column of evidence with the prose alongside both.
      strip.classList.add('skin-cad--docked')
      card.append(strip, body)
    } else {
      card.append(body)
      if (strip) card.append(strip)
    }

    const content = document.createElement('div')
    content.className = 'skin-step__content'
    content.append(card)
    item.append(content)
    list.append(item)
  }

  return list
}
